import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FerienHauserHometogo } from '@app/models/ferienhauser/Ferienhauser';
import { MapService } from '@app/services/map.service';

@Component({
    selector: 'clever-ferienhauserofferdetail',
    templateUrl: './ferienhauserofferdetail.component.html',
    styleUrls: ['./ferienhauserofferdetail.component.scss'],
    standalone: false
})
export class FerienhauserofferdetailComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() ferienHauser: FerienHauserHometogo;

    map = null;
    showMore: boolean = false;
    isBrowser: boolean = false;
    desktopTemplate: string = 'xl';
    destroyRef = inject(DestroyRef);

    constructor(
        protected mediaObserver: BreakpointObserver,
        protected mapService: MapService,
        protected cdr: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnDestroy(): void {
    }

    ngOnInit(): void {
        if (this.isBrowser) {
            this.initMedia();
        }
    }

    ngAfterViewInit(): void {
        if (this.isBrowser) {
            setTimeout(() => {
                this.onMapReady();
            }, 500);
        }
    }

    async initMedia() {
        if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.desktopTemplate = 'xs';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.desktopTemplate = 'sm';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.desktopTemplate = 'md';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.desktopTemplate = 'lg';
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.desktopTemplate = 'xl';
        }
        this.cdr.detectChanges();
    }

    async showMoreDesc() {
        this.showMore = !this.showMore;
        this.cdr.detectChanges();
    }

    async onMapReady() {
        if (isPlatformBrowser(this.platformId)) {
            if (this.ferienHauser?.geoLocation?.lat) {
                this.mapService.Leaflet$.pipe(
                    takeUntilDestroyed(this.destroyRef)
                ).subscribe(Leaflet => {
                    if (Leaflet) {
                        setTimeout(() => {
                            try {
                                this.map = Leaflet?.map('map').setView([this.ferienHauser?.geoLocation?.lat, this.ferienHauser?.geoLocation?.lon], 15);
                                if (this.map) {
                                    Leaflet.tileLayer('https://{s}.tile.openstreetmap.de/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' }).addTo(this.map);
                                    this.map?.invalidateSize();
                                    this.initMaps(Leaflet);
                                }
                            } catch (error) {
                                // console.log(error)
                            }
                        }, 300);
                    }
                });
            }
        }
    }

    async initMaps(Leaflet) {
        Leaflet.marker([Number(this.ferienHauser?.geoLocation?.lat), Number(this.ferienHauser?.geoLocation?.lon)],
            {
                icon: Leaflet.icon({
                    iconSize: [25, 41],
                    iconAnchor: [13, 41],
                    iconUrl: 'leaflet/marker-icon.png',
                    shadowUrl: 'leaflet/marker-shadow.png'
                })
            }
        ).bindPopup(this.ferienHauser?.title).addTo(this.map);
        this.map?.invalidateSize();
    }

    async gotoMap() {
        const mymap = document.getElementById('map');
        if (mymap) {
            mymap.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }

    amenities = {
        ACCESSIBLE: {
            text: '',
            ico: ''
        },
        // Wheelchair access

        AIR_CONDITIONING: {
            text: 'Klimaanlage',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="air_conditioning" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M15 32v-4.857l-2.97 2.227-1.2-1.6L15 24.643v-6.91l-5.985 3.455-.624 5.176-1.985-.24.444-3.686-4.206 2.428-1-1.732 4.207-2.429-3.417-1.46.786-1.838 4.796 2.048L14 16l-5.986-3.456-4.795 2.048-.786-1.839 3.416-1.459-4.206-2.428 1-1.732L6.85 9.562l-.444-3.687 1.986-.24.623 5.177L15 14.268v-6.91l-4.171-3.13 1.2-1.6L15 4.859V0h2v4.857l2.972-2.228 1.2 1.6L17 7.357v6.911l5.985-3.455.624-5.177 1.986.24-.445 3.687 4.207-2.429 1 1.732-4.207 2.429 3.416 1.46-.786 1.838-4.795-2.048L18 16l5.986 3.456 4.794-2.049.786 1.84-3.415 1.459 4.206 2.428-1 1.732-4.207-2.429.445 3.688-1.986.24-.624-5.178L17 17.732v6.91l4.172 3.13-1.2 1.6L17 27.141V32h-2Z"></path></svg>'
        },
        // Air conditioning

        ALLERGY_FRIENDLY: {
            text: '',
            ico: ''
        },
        // Allergy-friendly

        BALCONY: {
            text: 'Balkon',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="balcony" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M8.462 1a1 1 0 0 0-1 1v14.077H2v2h2.23V30a1 1 0 0 0 1 1h21.54a1 1 0 0 0 1-1V18.077H30v-2h-5.462V2a1 1 0 0 0-1-1H8.462ZM6.23 29V18.077h3.384V29H6.231Zm5.384 0H15V18.077h-3.385V29ZM17 29h3.385V18.077H17V29Zm8.77 0h-3.385V18.077h3.384V29ZM9.461 16.077v-5.539H15v5.539H9.462Zm7.538 0h5.538v-5.539H17v5.539Zm0-7.539h5.538V3H17v5.538Zm-2 0H9.462V3H15v5.538Z" clip-rule="evenodd"></path></svg>'
        },
        // Balcony

        BARBECUE: {
            text: 'Grill',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="barbecue" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M12.674 4.414c0-1.1-.672-1.697-1.85-2.57l1.25-1.688.021.016c1.097.812 2.68 1.984 2.68 4.242 0 .666-.227 1.237-.494 1.706-.254.444-.58.856-.85 1.196l-.022.027c-.295.372-.526.666-.693.958-.16.28-.217.488-.217.665 0 1.099.672 1.696 1.85 2.57l-1.25 1.687-.022-.016c-1.096-.812-2.68-1.984-2.68-4.241 0-.666.227-1.238.495-1.706.253-.445.58-.857.85-1.196l.022-.028c.295-.371.526-.666.692-.957.16-.28.218-.488.218-.665Zm6.827 0c0-1.1-.672-1.697-1.85-2.57L18.9.156l.022.016c1.096.812 2.68 1.984 2.68 4.242 0 .666-.227 1.237-.495 1.706-.253.444-.58.856-.85 1.196l-.022.027c-.295.372-.526.666-.692.958-.16.28-.218.488-.218.665 0 1.099.672 1.696 1.851 2.57l-1.25 1.687-.022-.016c-1.097-.812-2.68-1.984-2.68-4.241 0-.666.227-1.238.494-1.706.254-.445.58-.857.85-1.196l.022-.028c.295-.371.526-.666.693-.957.16-.28.217-.488.217-.665Z"></path><path fill-rule="evenodd" d="M2.345 16.843h2.363v.088a11.28 11.28 0 0 0 4.996 9.375l-1.355 5.418h2.215l1.092-4.367c1.03.43 2.136.71 3.294.818v3.55h2.1v-3.55a11.225 11.225 0 0 0 3.294-.818l1.092 4.367h2.215l-1.355-5.418a11.28 11.28 0 0 0 4.996-9.375v-.088h2.363v-2.1H2.345v2.1Zm4.464.088v-.088h18.382v.088a9.191 9.191 0 0 1-18.382 0Z" clip-rule="evenodd"></path></svg>'
        },
        // Grill

        BEACH_ACCESS: {
            text: '',
            ico: ''
        },
        // Beach access

        BICYCLE_FREE: {
            text: '',
            ico: ''
        },
        // Free bicycle

        BICYCLE_RENTAL: {
            text: '',
            ico: ''
        },
        // Bike rental

        BICYCLE_STORAGE: {
            text: '',
            ico: ''
        },
        // Bike storage

        BIO_GARBAGE_AVAILABLE: {
            text: '',
            ico: ''
        },
        // Compost bin available

        BLENDER: {
            text: 'Mixer',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="blender" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M11.47 25.824h3.353v2h-3.352v-2Zm10.118 0h-3.353v2h3.353v-2Z"></path><path fill-rule="evenodd" d="M7.593 6.176c.423 5.556 1.584 10.102 3.47 15.589H9.557c-1.15 0-2.132.831-2.321 1.966l-.902 5.412a2.353 2.353 0 0 0 2.32 2.74h15.748a2.353 2.353 0 0 0 2.32-2.74l-.901-5.412a2.353 2.353 0 0 0-2.321-1.966h-1.505c2.425-7.06 3.651-12.588 3.651-20.647a1 1 0 0 0-1-1H8.412a1 1 0 0 0-1 1c0 1.06.02 2.077.063 3.058H4.353A2.353 2.353 0 0 0 2 6.53v12.177h2V6.529c0-.194.158-.353.353-.353h3.24Zm1.825-4.058c.098 7.325 1.282 12.458 3.577 19.116.109.315.409.53.75.53h5.568a.792.792 0 0 0 .75-.53c2.296-6.66 3.48-11.82 3.577-19.116H9.418Zm.14 21.647H23.5c.172 0 .32.125.348.295l.902 5.411a.353.353 0 0 1-.348.411H8.656a.353.353 0 0 1-.348-.41l.902-5.412a.353.353 0 0 1 .348-.295Z" clip-rule="evenodd"></path></svg>'
        },
        // Blender

        BOARD_GAMES: {
            text: '',
            ico: ''
        },
        // Board games

        BOOK: {
            text: '',
            ico: ''
        },
        // Books available

        BREAKFAST: {
            text: '',
            ico: ''
        },
        // Breakfast

        BREAKFAST_INCLUDED: {
            text: '',
            ico: ''
        },
        // Breakfast included

        BREAKFAST_SURCHARGE: {
            text: '',
            ico: ''
        },
        // Breakfast (surcharge)

        CARBON_MONOXIDE_DETECTOR: {
            text: '',
            ico: ''
        },
        // Carbon monoxide detector

        CD_PLAYER: {
            text: 'CD Player',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="cd_player" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M18.095 20.143h-4.19v-2h4.19v2Zm-6.381-3.286h8.572v-2h-8.572v2Zm-6.571 6.619a2.095 2.095 0 1 1 4.19 0 2.095 2.095 0 0 1-4.19 0Zm19.619-2.095a2.095 2.095 0 1 0 0 4.19 2.095 2.095 0 0 0 0-4.19Z"></path><path fill-rule="evenodd" d="M10.524 4a3.19 3.19 0 0 0-3.19 3.19v3.286H6.142a5.38 5.38 0 0 0-5.381 5.381v7.667A6.476 6.476 0 0 0 10.62 29h10.758a6.476 6.476 0 0 0 9.858-5.476l.001-7.667a5.38 5.38 0 0 0-5.38-5.38h-1.191V7.19A3.19 3.19 0 0 0 21.477 4H10.523Zm12.143 6.476V7.19A1.19 1.19 0 0 0 21.477 6H10.523a1.19 1.19 0 0 0-1.19 1.19v3.286h13.333Zm6.571 5.381v2.939A6.476 6.476 0 0 0 19.328 27h-6.655a6.476 6.476 0 0 0-9.91-8.204v-2.939a3.381 3.381 0 0 1 3.38-3.38h19.714a3.38 3.38 0 0 1 3.381 3.38ZM7.238 19a4.476 4.476 0 1 0 0 8.952 4.476 4.476 0 0 0 0-8.952Zm13.048 4.476a4.476 4.476 0 1 1 8.952 0 4.476 4.476 0 0 1-8.952 0Z" clip-rule="evenodd"></path></svg>'
        },
        // CD player

        CHARGER_ELECTRIC_BIKE: {
            text: '',
            ico: ''
        },
        // Ebike charger

        CHARGER_ELECTRIC_CAR: {
            text: '',
            ico: ''
        },
        // EV charging station

        CHEF: {
            text: '',
            ico: ''
        },
        // Chef service

        CHILDREN_FRIENDLY: {
            text: 'Familienfreundlich',
            ico: '<svg width="16" height="16" viewBox="0 0 16 16" icon="FamilyFriendly" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M5.544 10.492c0 .123.094.277.22.339.187.092.406 0 .5-.185l.845-1.6V13.6c0 .215.188.4.407.4a.41.41 0 0 0 .407-.4v-2.338h.188V13.6c0 .215.187.4.407.4a.41.41 0 0 0 .407-.4V9.046l.845 1.6c.094.185.312.277.5.185 0-.031.032-.031.032-.031.156-.123.219-.308.125-.462l-.595-1.262c-.344-.677-.563-1.138-1.22-1.138h-1.19c-.656 0-.875.461-1.22 1.138l-.594 1.262a.233.233 0 0 0-.063.154zm2.472-2.707a.9.9 0 0 0 .907-.893c0-.492-.407-.861-.907-.861-.501 0-.908.369-.908.861a.9.9 0 0 0 .908.893zM6.92 7.692l-.5-1.077c-.501-1.016-.846-1.692-1.847-1.692H2.822c-1.001 0-1.346.676-1.846 1.692L.068 8.492a.523.523 0 0 0 .25.738c.282.123.626.031.751-.246l1.283-2.43v6.83a.59.59 0 0 0 .595.584c.345 0 .626-.246.626-.584V9.876h.282v3.508c0 .338.25.615.594.615a.622.622 0 0 0 .626-.615v-6.83l1.001 2c.22-.4.438-.739.845-.862zM3.698 4.677c.75 0 1.377-.585 1.377-1.323C5.075 2.615 4.449 2 3.698 2a1.35 1.35 0 0 0-1.346 1.354c0 .738.595 1.323 1.346 1.323zm12.233 3.815-.907-1.877c-.5-1.016-.845-1.692-1.846-1.692h-1.752c-1.002 0-1.346.676-1.847 1.692l-.5 1.077c.407.123.626.4.845.8l1.032-1.939-.594 2.893.313.676c.156.308.031.77-.25.923-.063.031-.126.031-.188.062h.72v2.277c0 .338.25.584.594.584.345 0 .626-.246.626-.584v-2.277h.25v2.277c0 .338.282.615.626.615.344 0 .595-.277.595-.615v-2.277h.97l-.97-4.554 1.283 2.431c.125.277.47.37.75.246a.524.524 0 0 0 .25-.738zm-3.629-3.815c.75 0 1.346-.585 1.346-1.323A1.35 1.35 0 0 0 12.302 2c-.75 0-1.377.615-1.377 1.354 0 .738.626 1.323 1.377 1.323z"></path></svg>'
        },
        // Family-friendly

        CHILDREN_NOT_ALLOWED: {
            text: '',
            ico: ''
        },
        // Children not allowed

        CHRISTMAS: {
            text: '',
            ico: ''
        },
        // Christmas decoration

        COFFEE_MACHINE: {
            text: 'Kaffeemaschine',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="coffee_machine" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M5 2.8c0-.11.09-.2.2-.2h6.209a2.204 2.204 0 0 0-.009.2v2.4a2.2 2.2 0 0 0 2.2 2.2H15V10h2V7.4h1.4a2.2 2.2 0 0 0 2.2-2.2V2.8c0-.067-.003-.134-.009-.2H23.2v-2h-18A2.2 2.2 0 0 0 3 2.8v26.4a2.2 2.2 0 0 0 2.2 2.2H28v-2H5.2a.2.2 0 0 1-.2-.2V2.8Zm13.4-.2h-4.8a.2.2 0 0 0-.2.2v2.4c0 .11.09.2.2.2h4.8a.2.2 0 0 0 .2-.2V2.8a.2.2 0 0 0-.2-.2Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M7.8 12.4a1 1 0 0 1 1-1h14.4a1 1 0 0 1 1 1c0 .883-.016 1.751-.051 2.6H26.8a2.2 2.2 0 0 1 2.2 2.2v6h-2v-6a.2.2 0 0 0-.2-.2h-2.774c-.328 3.967-1.164 7.412-2.855 9.89-.413.605-1.09.91-1.757.91h-6.828a2.117 2.117 0 0 1-1.757-.91C8.51 23.494 7.8 18.282 7.8 12.4Zm2.007 1c.082 5.38.84 9.674 2.674 12.362.004.006.031.038.105.038h6.828c.074 0 .101-.031.105-.038 1.835-2.688 2.592-6.982 2.674-12.362H9.807Z" clip-rule="evenodd"></path></svg>'
        },
        // Coffee machine

        COMPUTER: {
            text: '',
            ico: ''
        },
        // Computer

        CORPORATE: {
            text: '',
            ico: ''
        },
        // Suitable for business

        COT: {
            text: 'Kinderbett',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="cot" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M1.154.833V31h2v-4.801H29V31h2V12.436a3.32 3.32 0 0 0-3.32-3.32H3.154V.832h-2Zm2 10.282V24.2h2.64V11.115h-2.64ZM15.077 24.2h-2.641V11.115h2.64V24.2Zm2 0h2.64V11.115h-2.64V24.2Zm7.282 0h-2.641V11.115h2.641V24.2Zm2 0H29V12.436c0-.73-.591-1.32-1.32-1.32h-1.321v13.083ZM10.436 11.115V24.2H7.795V11.115h2.64Z" clip-rule="evenodd"></path></svg>'
        },
        // Crib

        COURTYARD: {
            text: '',
            ico: ''
        },
        // Courtyard

        DESK: {
            text: '',
            ico: ''
        },
        // Desk

        DETACHED: {
            text: 'Freistehend',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="detached" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M17 15v15h11a4 4 0 0 0 4-4V4a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v11h15Zm2 13V13H4V4a2 2 0 0 1 2-2h22a2 2 0 0 1 2 2v22a2 2 0 0 1-2 2h-9Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M0 28V17h15v15H4a4 4 0 0 1-4-4Zm2-9h11v11H4a2 2 0 0 1-2-2v-9Z" clip-rule="evenodd"></path></svg>'
        },
        // Detached

        DISHWASHER: {
            text: 'Spülmaschine',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="dishwasher" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M13.833 5.667h4.334v2h-4.334v-2Zm-7 0h4.334v2H6.833v-2Zm18.334 0h-4.334v2h4.334v-2Z"></path><path fill-rule="evenodd" d="M6.833 12.5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v4.667a4.502 4.502 0 0 1-3.5 4.388v2.778h2.5v2h-7v-2h2.5v-2.778a4.502 4.502 0 0 1-3.5-4.388V12.5Zm2 1v3.667a2.5 2.5 0 0 0 5 0V13.5h-5Zm9.667 13h2v-1.333h.167a4.5 4.5 0 0 0 4.5-4.5v-3.5a4.5 4.5 0 0 0-4.5-4.5H20.5v-1.334h-2V26.5Zm2-11.833v8.5h.167a2.5 2.5 0 0 0 2.5-2.5v-3.5a2.5 2.5 0 0 0-2.5-2.5H20.5Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M4.556 1A2.556 2.556 0 0 0 2 3.556v24.888A2.556 2.556 0 0 0 4.556 31h22.888A2.556 2.556 0 0 0 30 28.444V3.556A2.556 2.556 0 0 0 27.444 1H4.556ZM4 3.556C4 3.249 4.249 3 4.556 3h22.888c.307 0 .556.249.556.556v24.888a.556.556 0 0 1-.556.556H4.556A.556.556 0 0 1 4 28.444V3.556Z" clip-rule="evenodd"></path></svg>'
        },
        // Dishwasher

        DRYER: {
            text: 'Trockner',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="dryer" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M20.91 5.545h4.363v2h-4.364v-2Zm-2.364 9.745c0-1.031-.392-1.533-.947-2.19l1.529-1.29c.627.743 1.418 1.712 1.418 3.48 0 .939-.388 1.718-.666 2.278l-.03.058c-.311.628-.486 1.017-.486 1.447 0 1.032.391 1.534.946 2.191l-1.529 1.29c-.627-.743-1.417-1.712-1.417-3.481 0-.938.387-1.717.666-2.277l.029-.059c.311-.627.486-1.016.486-1.446ZM11.69 13.1c.555.657.946 1.159.946 2.19 0 .43-.175.82-.486 1.447l-.03.059c-.278.56-.665 1.339-.665 2.277 0 1.77.79 2.738 1.417 3.481l1.529-1.29c-.555-.657-.947-1.16-.947-2.191 0-.43.176-.82.487-1.447l.03-.058c.278-.56.665-1.34.665-2.277 0-1.77-.79-2.738-1.417-3.481L11.69 13.1Z"></path><path fill-rule="evenodd" d="M16 8a9.167 9.167 0 1 0 0 18.333A9.167 9.167 0 0 0 16 8Zm-7.167 9.167a7.167 7.167 0 1 1 14.334 0 7.167 7.167 0 0 1-14.334 0Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M2 3.556A2.556 2.556 0 0 1 4.556 1h22.888A2.556 2.556 0 0 1 30 3.556v24.888A2.556 2.556 0 0 1 27.444 31H4.556A2.556 2.556 0 0 1 2 28.444V3.556ZM4.556 3A.556.556 0 0 0 4 3.556v24.888c0 .307.249.556.556.556h22.888a.556.556 0 0 0 .556-.556V3.556A.556.556 0 0 0 27.444 3H4.556Z" clip-rule="evenodd"></path></svg>'
        },
        // Dryer

        DVD_PLAYER: {
            text: '',
            ico: ''
        },
        // DVD player

        EASTER: {
            text: '',
            ico: ''
        },
        // Special gift for Easter

        ECO_FRIENDLY_CLEANING_PRODUCTS: {
            text: '',
            ico: ''
        },
        // Eco-friendly cleaning products

        ECO_TOURISM: {
            text: '',
            ico: ''
        },
        // Eco-friendly

        ELDERLY_ACCESSIBLE: {
            text: '',
            ico: ''
        },
        // Accessible for elderly

        ELEVATOR: {
            text: '',
            ico: ''
        },
        // Elevator

        ENERGY_EFFICIENT_HEATING: {
            text: '',
            ico: ''
        },
        // Energy efficient heating

        ENERGY_EFFICIENT_LIGHTING: {
            text: '',
            ico: ''
        },
        // Energy efficient lighting

        FAN: {
            text: 'Ventilator',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="fan" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="m3.737 8.707 6.314 6.314a6.065 6.065 0 0 0-.012 1.877c-1.81.229-4.122.82-6.59 2.265-1.523.892-2.271 2.391-2.328 3.936-.057 1.526.555 3.103 1.727 4.275l.89.889a3.514 3.514 0 0 0 4.97 0l6.313-6.314a6.066 6.066 0 0 0 1.877.012c.228 1.81.82 4.123 2.265 6.59.891 1.523 2.391 2.272 3.936 2.329 1.526.056 3.103-.556 4.275-1.728l.889-.89a3.514 3.514 0 0 0 0-4.969l-6.314-6.314a6.065 6.065 0 0 0 .012-1.877c1.811-.228 4.123-.82 6.59-2.264 1.523-.892 2.271-2.392 2.328-3.937.057-1.526-.555-3.102-1.727-4.275l-.89-.889a3.514 3.514 0 0 0-4.97 0l-6.313 6.314a6.065 6.065 0 0 0-1.877-.012c-.228-1.81-.82-4.122-2.264-6.59-.892-1.523-2.392-2.271-3.937-2.328-1.526-.056-3.103.555-4.275 1.728l-.889.888a3.514 3.514 0 0 0 0 4.97Zm1.415-1.414 5.65 5.65a6.039 6.039 0 0 1 2.36-2.263c-.163-1.643-.66-3.845-2.05-6.22-.512-.873-1.35-1.306-2.285-1.34-.953-.036-1.995.351-2.787 1.143l-.888.889a1.514 1.514 0 0 0 0 2.141Zm13.904 3.51a6.026 6.026 0 0 1 2.264 2.36c1.643-.164 3.845-.66 6.22-2.051.873-.511 1.306-1.35 1.34-2.284.036-.953-.351-1.996-1.143-2.787l-.888-.89a1.514 1.514 0 0 0-2.142 0l-5.65 5.651ZM4.46 20.888c2.375-1.39 4.577-1.888 6.22-2.05a6.04 6.04 0 0 0 2.264 2.359l-5.651 5.65a1.514 1.514 0 0 1-2.141 0l-.89-.888c-.79-.792-1.178-1.834-1.143-2.787.035-.934.468-1.773 1.34-2.284Zm16.738-1.832a6.04 6.04 0 0 1-2.36 2.263c.163 1.643.66 3.845 2.05 6.22.512.873 1.35 1.306 2.285 1.34.953.036 1.995-.35 2.787-1.142l.889-.89a1.514 1.514 0 0 0 0-2.14l-5.651-5.651Zm-8.046-5.905a4.028 4.028 0 1 0 5.696 5.696 4.028 4.028 0 0 0-5.696-5.696Z" clip-rule="evenodd"></path></svg>'
        },
        // Fan

        HAS_FARM: {
            text: '',
            ico: ''
        },
        // Farm

        FENCED: {
            text: 'Umzäunt',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="fenced" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M16.625 3.22a1 1 0 0 0-1.25 0l-2.717 2.173a1 1 0 0 0-.375.78v4.436H9.39V6.174a1 1 0 0 0-.375-.781L6.299 3.219a1 1 0 0 0-1.25 0L2.332 5.393a1 1 0 0 0-.375.78v4.436H0v2h1.957v9.956H0v2h1.957V29a1 1 0 0 0 1 1H8.39a1 1 0 0 0 1-1v-4.435h2.892V29a1 1 0 0 0 1 1h5.434a1 1 0 0 0 1-1v-4.435h2.892V29a1 1 0 0 0 1 1h5.434a1 1 0 0 0 1-1v-4.435H32v-2h-1.956V12.61H32v-2h-1.956V6.174a1 1 0 0 0-.376-.781l-2.717-2.174a1 1 0 0 0-1.25 0l-2.717 2.174a1 1 0 0 0-.375.78v4.436h-2.892V6.174a1 1 0 0 0-.375-.781l-2.717-2.174ZM9.39 22.564h2.892V12.61H9.39v9.956Zm13.218 0h-2.892V12.61h2.892v9.956ZM14.283 28V6.655L16 5.28l1.717 1.374V28h-3.434Zm10.326 0V6.655l1.717-1.374 1.718 1.374V28h-3.435ZM3.957 28V6.655L5.674 5.28 7.39 6.655V28H3.957Z" clip-rule="evenodd"></path></svg>'
        },
        // Fenced

        FIREPLACE: {
            text: 'Kamin/Ofen',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="fireplace" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M16.925 2.38a1 1 0 0 0-1.396-1.262c-4.404 2.348-6.886 4.842-8.252 7.26C5.908 10.8 5.72 13.044 5.72 14.76c0 2.556 1.004 4.858 2.826 6.513 1.818 1.651 4.39 2.607 7.454 2.607s5.636-.956 7.454-2.607c1.822-1.655 2.826-3.957 2.826-6.513 0-3.615-1.926-7.058-5.126-8.977a1 1 0 0 0-1.029 0c-2.19 1.314-3.416 3.393-4.19 5.182-.5-2.046-.45-5.074.99-8.585ZM7.72 14.76c0-1.547.169-3.399 1.298-5.399.907-1.604 2.47-3.369 5.19-5.146-1.121 4.335-.436 8.027.984 9.974a1 1 0 0 0 1.747-.246l.12-.33c.676-1.87 1.55-4.284 3.587-5.777 2.286 1.605 3.634 4.214 3.634 6.924 0 2.012-.78 3.77-2.17 5.033-1.395 1.266-3.463 2.087-6.11 2.087-2.647 0-4.715-.82-6.11-2.087-1.39-1.263-2.17-3.021-2.17-5.033Z" clip-rule="evenodd"></path><path d="M6.634 26.256C3.885 25.012 1.62 23.52.31 22.513l1.22-1.586c1.203.926 3.337 2.334 5.928 3.507 2.598 1.175 5.594 2.086 8.542 2.086 2.948 0 5.944-.91 8.541-2.086 2.592-1.173 4.726-2.58 5.93-3.507l1.219 1.586c-1.31 1.007-3.575 2.5-6.324 3.743-.812.367-1.672.716-2.566 1.024V31h-2v-3.12c-1.55.396-3.17.64-4.8.64s-3.25-.244-4.8-.64V31h-2v-3.72a29.745 29.745 0 0 1-2.566-1.024Z"></path></svg>'
        },
        // Fireplace

        FIRE_EXTINGUISHER: {
            text: 'Feuerlöscher',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="fire_extinguisher" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M24.45 1.403A2 2 0 0 1 27 3.326v5.348a2 2 0 0 1-2.55 1.923l-5.725-1.636A1 1 0 0 1 18 8V7h-1v3.083c2.838.476 5 2.944 5 5.917v12a1 1 0 0 1-1 1H11a1 1 0 0 1-1-1V16a6.002 6.002 0 0 1 5-5.917V8.829A3.001 3.001 0 0 1 11.17 7H9a1 1 0 0 0-1 1.001v1H6V8a3 3 0 0 1 3-3h2.17a3.001 3.001 0 0 1 5.66 0H18V4a1 1 0 0 1 .725-.962l5.726-1.635ZM15 6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm5-1.246v2.492l5 1.428V3.326l-5 1.428ZM16 12a4 4 0 0 0-4 4v11h8V16a4 4 0 0 0-4-4Z" clip-rule="evenodd"></path></svg>'
        },
        // Fire extinguisher

        FISHING: {
            text: '',
            ico: ''
        },
        // Fishing

        FITNESS: {
            text: '',
            ico: ''
        },
        // Fitness

        FREEZER: {
            text: 'Gefrierfach',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="freezer" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M18.546 5.545h6.727v2h-6.727v-2ZM15 10.09v6.542l-5.664-3.27-1 1.731L14 18.363l-5.664 3.271 1 1.732L15 20.096v6.54h2v-6.54l5.664 3.27 1-1.732L18 18.364l5.664-3.27-1-1.733L17 16.631v-6.54h-2Z"></path><path fill-rule="evenodd" d="M2 3.394A2.576 2.576 0 0 1 4.576.818h22.848A2.576 2.576 0 0 1 30 3.394v25.212a2.576 2.576 0 0 1-2.576 2.576H4.576A2.576 2.576 0 0 1 2 28.606V3.394Zm2.576-.576A.576.576 0 0 0 4 3.394v25.212c0 .318.258.576.576.576h22.848a.576.576 0 0 0 .576-.576V3.394a.576.576 0 0 0-.576-.576H4.576Z" clip-rule="evenodd"></path></svg>'
        },
        // Freezer

        FRIDGE: {
            text: 'Kühlschrank',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="fridge" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M22 7h-6v2h6V7Zm-6 8h6v2h-6v-2Z"></path><path fill-rule="evenodd" d="M8.333 1A2.333 2.333 0 0 0 6 3.333v25.334A2.333 2.333 0 0 0 8.333 31h15.334A2.333 2.333 0 0 0 26 28.667V3.333A2.333 2.333 0 0 0 23.667 1H8.333ZM8 3.333C8 3.15 8.15 3 8.333 3h15.334c.184 0 .333.15.333.333V11H8V3.333ZM8 13v15.667c0 .184.15.333.333.333h15.334c.184 0 .333-.15.333-.333V13H8Z" clip-rule="evenodd"></path></svg>'
        },
        // Fridge

        GAMES: {
            text: '',
            ico: ''
        },
        // Games

        GAMES_CONSOLE: {
            text: '',
            ico: ''
        },
        // Game console

        GARAGE: {
            text: '',
            ico: ''
        },
        // Parking

        GARDEN: {
            text: '',
            ico: ''
        },
        // Yard

        GOLF: {
            text: '',
            ico: ''
        },
        // Golf

        HAIRDRYER: {
            text: 'Haartrockner',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="hairdryer" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M5.508 18.266c-1.176-.547-2.223-1.315-3.088-2.376a2.087 2.087 0 0 1-.462-1.322v-8.65c0-.454.142-.93.462-1.323C4.757 1.73 8.423 1 12.379 1h.003c7.458 0 13.746 2.406 17.734 5.128a2.14 2.14 0 0 1 .926 1.771v4.686a2.14 2.14 0 0 1-.926 1.772c-3.728 2.545-9.468 4.814-16.294 5.098.164.822.533 1.884 1.088 3.111.754 1.67 1.793 3.52 2.915 5.261.925 1.436-.055 3.434-1.845 3.434h-4.172a2.17 2.17 0 0 1-1.776-.92c-2.494-3.547-4.327-6.784-4.524-12.075Zm6.289 1.214c-1.483-.028-2.917-.17-4.247-.503.316 4.29 1.873 7.02 4.118 10.213a.17.17 0 0 0 .14.07h4.172a.168.168 0 0 0 .096-.025.223.223 0 0 0 .075-.084.23.23 0 0 0-.007-.24c-1.162-1.803-2.252-3.74-3.057-5.521-.622-1.378-1.113-2.744-1.29-3.91Zm1.582-2.01V3.015c6.602.203 12.118 2.382 15.609 4.765.024.017.054.056.054.12v4.685c0 .065-.03.104-.054.12-3.491 2.384-9.007 4.563-15.61 4.765ZM3.97 5.86c1.586-1.945 4.095-2.72 7.41-2.842v14.45c-3.314-.123-5.823-.897-7.409-2.841 0-.001-.012-.018-.012-.06v-.97H8v-2H3.958v-2.71H8v-2H3.958v-.969c0-.04.011-.058.012-.06Z" clip-rule="evenodd"></path></svg>'
        },
        // Hairdryer

        HAMMAM: {
            text: '',
            ico: ''
        },
        // Hammam

        HEATING: {
            text: 'Heizung',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="heating" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M12.667 4c0-1.17-.723-1.804-1.929-2.696l1.19-1.608.017.013C13.072.544 14.667 1.725 14.667 4c0 .665-.226 1.238-.496 1.71-.257.451-.59.87-.867 1.219l-.02.026c-.303.38-.543.687-.718.992-.167.294-.233.52-.233.72 0 1.17.724 1.803 1.929 2.696l-1.19 1.607-.017-.012c-1.127-.835-2.722-2.016-2.722-4.291 0-.665.226-1.238.496-1.711.257-.45.59-.869.867-1.218l.02-.027c.303-.38.543-.686.718-.991.167-.294.233-.52.233-.72Zm-7 21v-5.833h2V25h-2Zm4.666-5.833V25h2v-5.833h-2ZM19.667 25v-5.833h2V25h-2ZM15 19.167V25h2v-5.833h-2ZM24.333 25v-5.833h2V25h-2Z"></path><path fill-rule="evenodd" d="M4.333 14.667A3.333 3.333 0 0 0 1 18v8.167A3.333 3.333 0 0 0 4.333 29.5h2.5V32h2v-2.5h14.334V32h2v-2.5h2.5A3.333 3.333 0 0 0 31 26.167V18a3.333 3.333 0 0 0-3.333-3.333H4.333ZM24.173 27.5h3.494c.736 0 1.333-.597 1.333-1.333V18c0-.736-.597-1.333-1.333-1.333H4.333C3.597 16.667 3 17.264 3 18v8.167c0 .736.597 1.333 1.333 1.333h19.84Z" clip-rule="evenodd"></path><path d="M17.738 1.304c1.206.892 1.929 1.526 1.929 2.696 0 .2-.066.426-.234.72-.174.305-.414.611-.716.991l-.021.027c-.277.35-.61.767-.867 1.218-.27.473-.496 1.046-.496 1.71 0 2.276 1.595 3.457 2.722 4.292l.016.012 1.19-1.607c-1.204-.893-1.928-1.526-1.928-2.696 0-.2.066-.426.234-.72.174-.305.414-.612.716-.992l.021-.026c.277-.35.61-.768.867-1.218.27-.473.496-1.046.496-1.711 0-2.275-1.595-3.456-2.722-4.291l-.016-.013-1.19 1.608Z"></path></svg>'
        },
        // Heating

        HEN_STAG_PARTIES: {
            text: '',
            ico: ''
        },
        // Available for bachelor and bachelorette parties

        HIGH_CHAIR: {
            text: 'Kinderhochstuhl',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="high_chair" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M8.75 1.236h2.03l.593 3.41h7.115a3.205 3.205 0 0 1 3.205 3.204v4.512H24v2h-2.876c.062.154.113.314.152.48L24.794 30h-2.053l-1.583-6.82h-9.75L9.825 30H7.772l3.52-15.158c.15-.647.489-1.209.951-1.634a5.4 5.4 0 0 1-1.99-3.329l-.562-3.234h-.022v-.124l-.92-5.285Zm3.473 8.3-.502-2.89h6.767c.666 0 1.205.539 1.205 1.204v4.512h-4.125a3.41 3.41 0 0 1-3.345-2.825Zm3.342 4.826h2.598c.556.004 1.038.39 1.164.932l1.367 5.887h-8.821l1.366-5.887a1.205 1.205 0 0 1 1.174-.932h1.152Z" clip-rule="evenodd"></path></svg>'
        },
        // High chair

        HOUSEKEEPER: {
            text: '',
            ico: ''
        },
        // Housekeeping

        HOUSE_INSULATION: {
            text: '',
            ico: ''
        },
        // House insulation

        INTERNET: {
            text: 'WLAN',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="internet" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M29.991 12.556c-7.81-7.81-20.473-7.81-28.284 0L.293 11.142c8.591-8.591 22.521-8.591 31.113 0l-1.415 1.414Z"></path><path d="M25.749 16.799c-5.468-5.467-14.332-5.467-19.8 0l-1.413-1.414c6.248-6.249 16.379-6.249 22.627 0l-1.414 1.414Z"></path><path d="M10.192 21.042a8 8 0 0 1 11.314 0l1.414-1.415c-3.905-3.905-10.237-3.905-14.142 0l1.414 1.415Z"></path><path fill-rule="evenodd" d="M15.85 22.284a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z" clip-rule="evenodd"></path></svg>'
        },
        // WiFi

        INTERNET_HIGH_SPEED: {
            text: '',
            ico: ''
        },
        // High speed internet

        INTERNET_HIGH_SPEED_IN_ROOM: {
            text: '',
            ico: ''
        },
        // High speed internet in room

        IRON: {
            text: 'Bügeleisen',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="iron" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M29.857 20.484v-.95c0-.399-.1-.797-.292-1.15-2.726-5.024-5.683-8.092-9.89-9.85a20.95 20.95 0 0 0-2.844-.945 27.595 27.595 0 0 0-.596-.16l-.003.012c-3.53-.822-7.784-1.048-13.09-1.048a1 1 0 0 0-1 1v13.09a3.144 3.144 0 0 0 1 6.124h25.715a3.143 3.143 0 0 0 1-6.123ZM4.143 20.32h23.714v-.786a.422.422 0 0 0-.05-.197c-1.954-3.602-3.95-6.004-6.389-7.622-.06.468-.224.928-.495 1.337l-.24.361a3.143 3.143 0 0 1-2.62 1.407h-7.42A3.143 3.143 0 0 1 7.5 11.68V8.464h.099a115.19 115.19 0 0 0-3.456-.068V20.32ZM9.5 8.556c2.618.162 4.845.459 6.785.957 1.006.28 1.836.585 2.633.949.493.225.722.906.338 1.486l-.24.362a1.14 1.14 0 0 1-.952.511h-7.421A1.143 1.143 0 0 1 9.5 11.68V8.556ZM2 23.464c0-.63.512-1.143 1.143-1.143h25.714a1.143 1.143 0 0 1 0 2.286H3.143A1.143 1.143 0 0 1 2 23.464Z" clip-rule="evenodd"></path></svg>'
        },
        // Iron

        KETTLE: {
            text: 'Wasserkocher',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="kettle" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M13.533 7.5a1 1 0 0 0-1 1v16.467a1 1 0 0 0 1 1H18.6a1 1 0 0 0 1-1V8.5a1 1 0 0 0-1-1h-5.067Zm1 16.467V9.5H17.6v14.467h-3.067Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M4.667 31.033a1 1 0 0 1-1-1c0-9.081 1.893-17.054 4.49-23.066h-5.39a.267.267 0 0 0-.267.266v11.4h-2v-11.4a2.267 2.267 0 0 1 2.267-2.266H9.08a36.176 36.176 0 0 1 1.652-2.993c.247-.4.617-.713 1.062-.888 2.962-1.16 5.583-1.16 8.545 0 .446.175.815.489 1.062.888a36.21 36.21 0 0 1 1.652 2.993h4.898c1.684 0 2.78 1.773 2.027 3.28l-3.336 6.672c1.128 4.503 1.825 9.605 1.825 15.114a1 1 0 0 1-1 1h-22.8ZM25.84 12.048a49.94 49.94 0 0 0-1.864-5.081h3.974c.198 0 .327.208.238.386l-2.348 4.695ZM12.433 3.026C8.932 8.689 5.85 17.924 5.675 29.033h20.784C26.283 17.924 23.2 8.69 19.7 3.026a.185.185 0 0 0-.09-.077c-2.493-.976-4.593-.976-7.087 0a.185.185 0 0 0-.09.077Z" clip-rule="evenodd"></path></svg>'
        },
        // Kettle

        KIDS_ENTERTAINMENT: {
            text: '',
            ico: ''
        },
        // Kids entertainment

        KIDS_FURNITURE: {
            text: '',
            ico: ''
        },
        // Kids furniture

        KIDS_PLAYGROUND: {
            text: '',
            ico: ''
        },
        // Playground

        KIDS_SERVICES: {
            text: '',
            ico: ''
        },
        // Child-related services

        KING_SIZE_BED: {
            text: 'Breites Doppelbett (Kingsize)',
            ico: '<svg width="16" height="16" viewBox="0 0 16 16" icon="king_size_bed" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M13.012 5.247v-.579h1.362c.133 0 .249.093.279.224l1.34 5.873a.289.289 0 0 1-.28.355H.287a.289.289 0 0 1-.279-.355l1.34-5.873a.287.287 0 0 1 .279-.224h1.527v.579h-1.3L.646 10.54h14.708l-1.207-5.293h-1.135z"></path><path d="M.572 11.112v1.915c0 .235.305.461.722.461h13.412c.41 0 .722-.234.722-.46v-1.916H.572zM16 13.027c0 .59-.6 1.04-1.294 1.04H1.294c-.701 0-1.294-.438-1.294-1.04v-2.204c0-.16.128-.29.286-.29h15.428c.158 0 .286.13.286.29v2.204zM1.326 7.205a.29.29 0 1 1 0-.58h13.348a.29.29 0 1 1 0 .58H1.326zM6.757 4.76H3.43l-.17.883h3.47l.028-.883zm.253 1.462h-4.1a.29.29 0 0 1-.284-.344l.282-1.463a.29.29 0 0 1 .284-.235h3.863a.29.29 0 0 1 .29.299l-.046 1.463a.29.29 0 0 1-.29.28zm2.148 0a.29.29 0 0 1-.29-.282l-.037-1.462a.29.29 0 0 1 .289-.298h3.863a.29.29 0 0 1 .284.235l.282 1.463a.29.29 0 0 1-.284.344H9.158zm3.756-.579-.17-.883H9.417l.023.883h3.474z"></path><path d="M1.813 4.957a.29.29 0 1 1-.579 0V3.75c0-.59.607-1.04 1.31-1.04h10.888c.714 0 1.334.44 1.334 1.04v1.207a.29.29 0 1 1-.58 0V3.75c0-.23-.325-.46-.754-.46H2.545c-.415 0-.732.234-.732.46v1.207zm.122 8.883a.29.29 0 1 1 .58 0v.487a.29.29 0 1 1-.58 0v-.487zm11.551 0a.29.29 0 1 1 .579 0v.487a.29.29 0 1 1-.58 0v-.487z"></path><path d="M7.055 5.3a.29.29 0 1 1 0-.58H9.12a.29.29 0 0 1 0 .58H7.055z"></path></svg>'
        },
        // King-size bed

        KITCHEN: {
            text: 'Küche',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="kitchen" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M7.64 28.18v-6.96h-2v6.96h2Zm4.64-6.96v6.96h-2v-6.96h2Z"></path><path fill-rule="evenodd" d="M5.64 3.82a3.32 3.32 0 1 1 6.64 0v4.64a3.321 3.321 0 0 1-2.32 3.167V17.9h3.221a2.16 2.16 0 0 1 2.136 1.84l1.392 9.28a2.16 2.16 0 0 1-2.136 2.48H3.347a2.16 2.16 0 0 1-2.136-2.48l1.392-9.28a2.16 2.16 0 0 1 2.136-1.84H7.96v-6.273A3.322 3.322 0 0 1 5.64 8.46V3.82ZM8.96 2.5a1.32 1.32 0 0 0-1.32 1.32v4.64a1.32 1.32 0 1 0 2.64 0V3.82A1.32 1.32 0 0 0 8.96 2.5Zm5.613 27a.16.16 0 0 0 .158-.184l-1.392-9.28a.16.16 0 0 0-.158-.136H4.739a.16.16 0 0 0-.158.136l-1.392 9.28a.16.16 0 0 0 .158.184h11.226Zm7.763-29a5.64 5.64 0 0 0-5.596 6.34l.707 5.656a4.48 4.48 0 0 0 4.445 3.924h1.148v3.953a3.322 3.322 0 0 0-2.32 3.167v4.64a3.32 3.32 0 1 0 6.64 0v-4.64a3.322 3.322 0 0 0-2.32-3.167V16.42h1.148a4.48 4.48 0 0 0 4.445-3.924l.707-5.656A5.64 5.64 0 0 0 25.744.5h-3.408Zm-3.612 6.091A3.642 3.642 0 0 1 20.96 2.77a3.22 3.22 0 0 0-.142 1.09l.411 9.864c.01.218.043.43.099.632a2.48 2.48 0 0 1-1.896-2.107l-.707-5.657Zm4.503 7.049a.814.814 0 0 0 1.626 0l.411-9.864a1.225 1.225 0 1 0-2.448 0l.41 9.864Zm3.625.083a2.82 2.82 0 0 1-.099.632 2.48 2.48 0 0 0 1.896-2.107l.707-5.657A3.642 3.642 0 0 0 27.12 2.77c.107.343.157.71.142 1.09l-.411 9.864ZM22.72 23.54a1.32 1.32 0 0 1 2.64 0v4.64a1.32 1.32 0 0 1-2.64 0v-4.64Z" clip-rule="evenodd"></path></svg>'
        },
        // Kitchen

        LEISURE_ACTIVITIES: {
            text: '',
            ico: ''
        },
        // Leisure activities

        LINEN: {
            text: 'Bettwäsche',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="Linen" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M8.44 1A7.48 7.48 0 0 0 .96 8.48v14.04A7.48 7.48 0 0 0 8.44 30h17.28a5.32 5.32 0 0 0 3.766-9.078A3.159 3.159 0 0 0 31.04 18.2V8.48A7.48 7.48 0 0 0 23.56 1H8.44Zm20.6 14.26V8.48A5.48 5.48 0 0 0 23.56 3H8.44a5.48 5.48 0 0 0-5.48 5.48v8.949a7.46 7.46 0 0 1 5.48-2.389h19.44c.41 0 .8.078 1.16.22Zm0 2.94c0 .64-.52 1.16-1.16 1.16H8.44a3.16 3.16 0 0 0-.005 6.32H25.72a1 1 0 1 0 0-2H8.44a1.16 1.16 0 0 1 0-2.32h17.28a3.32 3.32 0 1 1 0 6.64H8.44a5.48 5.48 0 0 1 0-10.96h19.44c.64 0 1.16.52 1.16 1.16Z" clip-rule="evenodd"></path></svg>'
        },
        // Bed linen

        LINEN_FREE: {
            text: 'Bettwäsche (ohne Gebühr)',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="Linen" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M8.44 1A7.48 7.48 0 0 0 .96 8.48v14.04A7.48 7.48 0 0 0 8.44 30h17.28a5.32 5.32 0 0 0 3.766-9.078A3.159 3.159 0 0 0 31.04 18.2V8.48A7.48 7.48 0 0 0 23.56 1H8.44Zm20.6 14.26V8.48A5.48 5.48 0 0 0 23.56 3H8.44a5.48 5.48 0 0 0-5.48 5.48v8.949a7.46 7.46 0 0 1 5.48-2.389h19.44c.41 0 .8.078 1.16.22Zm0 2.94c0 .64-.52 1.16-1.16 1.16H8.44a3.16 3.16 0 0 0-.005 6.32H25.72a1 1 0 1 0 0-2H8.44a1.16 1.16 0 0 1 0-2.32h17.28a3.32 3.32 0 1 1 0 6.64H8.44a5.48 5.48 0 0 1 0-10.96h19.44c.64 0 1.16.52 1.16 1.16Z" clip-rule="evenodd"></path></svg>'
        },
        // Bed linen (free)

        LINEN_SURCHARGE: {
            text: '',
            ico: ''
        },
        // Bed linen (surcharge)

        MICROWAVE: {
            text: 'Mikrowelle',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="microwave" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M4.286 11.976a2.333 2.333 0 0 1 2.333-2.333h12.333a2.333 2.333 0 0 1 2.334 2.333v8.048a2.333 2.333 0 0 1-2.334 2.333H6.62a2.333 2.333 0 0 1-2.333-2.333v-8.048Zm2.333-.333a.333.333 0 0 0-.333.333v8.048c0 .184.149.333.333.333h12.333c.184 0 .334-.15.334-.333v-8.048a.333.333 0 0 0-.334-.333H6.62Z" clip-rule="evenodd"></path><path d="M27.714 10.714h-4.143v2h4.143v-2ZM23.571 15h4.143v2h-4.143v-2Zm4.143 4.285h-4.143v2h4.143v-2Z"></path><path fill-rule="evenodd" d="M29.667 5.357A2.333 2.333 0 0 1 32 7.69v16.62a2.333 2.333 0 0 1-2.333 2.333H2.333A2.333 2.333 0 0 1 0 24.309V7.69a2.333 2.333 0 0 1 2.333-2.333h27.334ZM30 7.69a.333.333 0 0 0-.333-.333H2.333A.333.333 0 0 0 2 7.69v16.62c0 .183.15.333.333.333h27.334c.184 0 .333-.15.333-.334V7.69Z" clip-rule="evenodd"></path></svg>'
        },
        // Microwave

        MINIBAR: {
            text: '',
            ico: ''
        },
        // Minibar

        MINI_GOLF: {
            text: '',
            ico: ''
        },
        // Mini golf

        NATURISM: {
            text: '',
            ico: ''
        },
        // Naturist area

        NO_CAR_NEEDED: {
            text: '',
            ico: ''
        },
        // No car needed

        NO_DOGS: {
            text: 'Haustiere nicht gestattet',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="no_dogs" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="m2.293 3.707 26 26 1.414-1.414-26-26-1.414 1.414Z"></path><path fill-rule="evenodd" d="m22.737 19.201.23-2.753 1.34-1.34 3.45.862a1 1 0 0 0 1.075-.415l2-3a1 1 0 0 0-.277-1.387l-5.476-3.65.411-2.877a1 1 0 0 0-.747-1.111l-2-.5a1 1 0 0 0-1.127.502l-4.282 8.088-1.794.385 1.684 1.684.865-.185 2.876 2.875-.08.97 1.852 1.852ZM22 14.586l-2.598-2.598 3.615-6.828.366.091-.373 2.608a1 1 0 0 0 .435.973l5.168 3.445-1.054 1.582-3.317-.83a1 1 0 0 0-.95.264L22 14.586Z" clip-rule="evenodd"></path><path d="m22.193 25.728-1.853-1.852L20.08 27h-2.233l-.86-5.164A1 1 0 0 0 16 21h-6a1 1 0 0 0-.986.836L8.153 27h-2.23l-.856-10.706 6.334-1.357-1.684-1.685-5.345 1.146-2.613-3.049-1.518 1.302 2.788 3.253.974 12.176A1 1 0 0 0 5 29h4a1 1 0 0 0 .986-.836L10.847 23h4.306l.86 5.164A1 1 0 0 0 17 29h4a1 1 0 0 0 .997-.917l.196-2.355Z"></path></svg>'
        },
        // No pets

        NO_SINGLE_USE_PLASTIC: {
            text: '',
            ico: ''
        },
        // No single-use plastic

        ORGANIC_FOOD: {
            text: '',
            ico: ''
        },
        // Organic food

        OVEN: {
            text: '',
            ico: ''
        },
        // Oven

        PARKING: {
            text: 'Parken',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="parking" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M11.692 7.462a1 1 0 0 0-1 1v17.23h2v-5.461h4.385a6.385 6.385 0 1 0 0-12.77h-5.385Zm5.385 10.769h-4.385V9.46h4.385a4.385 4.385 0 1 1 0 8.77Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M16 1C7.716 1 1 7.716 1 16c0 8.284 6.716 15 15 15 8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15ZM3 16C3 8.82 8.82 3 16 3s13 5.82 13 13-5.82 13-13 13S3 23.18 3 16Z" clip-rule="evenodd"></path></svg>'
        },
        // Parking

        PATIO: {
            text: 'Terrasse',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="patio" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M4.214-.107c-.478 0-.899.317-1.03.777l-2.143 7.5a1.07 1.07 0 0 0-.04.23H1v1.136a4.286 4.286 0 0 0 7.5 2.835 4.276 4.276 0 0 0 3.214 1.45c1.28 0 2.43-.56 3.215-1.45a4.275 4.275 0 0 0 3.214 1.45c1.28 0 2.429-.56 3.214-1.45a4.286 4.286 0 0 0 7.5-2.835V8.4h-.002a1.067 1.067 0 0 0-.04-.23L26.674.67a1.072 1.072 0 0 0-1.03-.777H4.214ZM3.143 9.536h23.571a2.143 2.143 0 0 1-4.285 0h-2.143a2.143 2.143 0 1 1-4.286 0h-2.143a2.143 2.143 0 1 1-4.286 0H7.43a2.143 2.143 0 1 1-4.286 0Zm.349-2.143 1.53-5.357h19.813l1.53 5.357H3.492ZM1 18.036a1.071 1.071 0 0 0-1.057 1.247L1 25.624v5.269h2.143v-4.286h1.071c.592 0 1.072.48 1.072 1.072v3.214h2.143v-3.214c0-.376-.065-.737-.183-1.072h5.54v4.286h2.143v-4.286H16c.592 0 1.071.48 1.071 1.072v3.214h2.143v-3.214A3.214 3.214 0 0 0 16 24.464h-1.235l-.922-5.533a1.071 1.071 0 0 0-1.057-.895H1Zm11.592 6.428H2.98l-.714-4.285h9.613l.714 4.285Z" clip-rule="evenodd"></path><path d="M32 22.321h-3.577l1.285 8.572h-2.166l-1.286-8.572h-1.369l-1.286 8.572h-2.166l1.285-8.572h-3.577V20.18H32v2.142Z"></path></svg>'
        },
        // Patio

        PETANQUE: {
            text: '',
            ico: ''
        },
        // Petanque

        PETS_ALLOWED: {
            text: '',
            ico: ''
        },
        // Pet-friendly

        POOL: {
            text: '',
            ico: ''
        },
        // Pool

        POOL_HEATED: {
            text: 'Beheizter Pool',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="pool_heated" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M28.5 1.25a3.5 3.5 0 0 1 3.5 3.5V6h-2V4.75a1.5 1.5 0 0 0-3 0V23.5h-2v-2.75h-8v2.75h-2V4.75a3.5 3.5 0 1 1 7 0V6h-2V4.75a1.5 1.5 0 0 0-3 0v4h8v-4a3.5 3.5 0 0 1 3.5-3.5Zm-3.5 9.5h-8v3h8v-3Zm0 5v3h-8v-3h8Z" clip-rule="evenodd"></path><path d="M4.75 28.75A2.75 2.75 0 0 0 7.5 26h2a2.75 2.75 0 1 0 5.5 0h2a2.75 2.75 0 1 0 5.5 0h2a2.75 2.75 0 1 0 5.5 0h2a4.75 4.75 0 0 1-8.5 2.916 4.742 4.742 0 0 1-3.75 1.834A4.742 4.742 0 0 1 16 28.916a4.742 4.742 0 0 1-3.75 1.834 4.742 4.742 0 0 1-3.75-1.834A4.75 4.75 0 0 1 0 26h2a2.75 2.75 0 0 0 2.75 2.75ZM2.5 13.5c0-1.293-.807-1.992-2.095-2.946l1.19-1.608.013.01C2.818 9.852 4.5 11.098 4.5 13.5c0 .696-.236 1.297-.522 1.798-.273.478-.626.922-.924 1.297l-.02.027c-.324.406-.585.74-.775 1.071-.183.322-.259.577-.259.807 0 1.294.807 1.992 2.095 2.947l-1.19 1.607-.013-.01C1.682 22.148 0 20.902 0 18.5c0-.696.236-1.297.522-1.797.273-.479.626-.923.924-1.298l.02-.027c.324-.406.585-.739.775-1.071.183-.322.259-.577.259-.807Zm4.405-2.946C8.193 11.508 9 12.206 9 13.5c0 .23-.076.486-.26.807-.189.332-.45.665-.773 1.071l-.021.027c-.298.375-.651.82-.924 1.298-.286.5-.522 1.101-.522 1.797 0 2.402 1.682 3.648 2.892 4.544l.013.01 1.19-1.607C9.307 20.492 8.5 19.794 8.5 18.5c0-.23.076-.485.26-.807.189-.332.45-.665.773-1.071l.021-.027c.298-.375.651-.82.924-1.297.286-.501.522-1.102.522-1.798 0-2.402-1.682-3.648-2.892-4.544l-.013-.01-1.19 1.608Z"></path></svg>'
        },
        // Heated pool

        POOL_PRIVATE: {
            text: 'Privater Pool',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="PrivatePool" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M28.5 1.25a3.5 3.5 0 0 1 3.5 3.5V6h-2V4.75a1.5 1.5 0 0 0-3 0V23.5h-2v-2.75h-8v2.75h-2V4.75a3.5 3.5 0 1 1 7 0V6h-2V4.75a1.5 1.5 0 0 0-3 0v4h8v-4a3.5 3.5 0 0 1 3.5-3.5Zm-3.5 9.5h-8v3h8v-3Zm0 5v3h-8v-3h8Z" clip-rule="evenodd"></path><path d="M4.75 28.75A2.75 2.75 0 0 0 7.5 26h2a2.75 2.75 0 1 0 5.5 0h2a2.75 2.75 0 1 0 5.5 0h2a2.75 2.75 0 1 0 5.5 0h2a4.75 4.75 0 0 1-8.5 2.916 4.742 4.742 0 0 1-3.75 1.834A4.742 4.742 0 0 1 16 28.916a4.742 4.742 0 0 1-3.75 1.834 4.742 4.742 0 0 1-3.75-1.834A4.75 4.75 0 0 1 0 26h2a2.75 2.75 0 0 0 2.75 2.75Z"></path></svg>'
        },
        // Private pool

        POOL_SHARED: {
            text: '',
            ico: ''
        },
        // Shared pool

        POOL_TABLE: {
            text: '',
            ico: ''
        },
        // Pool table

        PUBLIC_TRANSPORTATION_NEARBY: {
            text: '',
            ico: ''
        },
        // Public transportation nearby

        QUEEN_SIZE_BED: {
            text: 'Doppelbett (Queensize)',
            ico: '<svg width="16" height="16" viewBox="0 0 16 16" icon="queen_size_bed" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M7.722 3.54H3.955l-.163 1.17h3.905l.025-1.17zm.318 1.87H3.39a.35.35 0 0 1-.347-.398l.26-1.87a.35.35 0 0 1 .347-.302h4.43a.35.35 0 0 1 .35.357l-.04 1.87a.35.35 0 0 1-.35.343z"></path><path d="M8.06 5.41a.35.35 0 0 1-.35-.343l-.04-1.87a.35.35 0 0 1 .35-.357h4.43a.35.35 0 0 1 .347.302l.26 1.87a.35.35 0 0 1-.347.398H8.06zm4.248-.7-.163-1.17H8.378l.025 1.17h3.905z"></path><path d="m15.222 10.96-1.204-7.092a.35.35 0 0 1 .293-.403.356.356 0 0 1 .41.287l1.274 7.5a.352.352 0 0 1-.352.408H.357a.352.352 0 0 1-.352-.408l1.274-7.5a.356.356 0 0 1 .41-.287.35.35 0 0 1 .293.403L.778 10.96h14.444z"></path><path d="M15.287 11.66H.713v2.466a.596.596 0 0 0 .602.584h13.37c.33 0 .597-.26.602-.582V11.66zM16 14.13a1.304 1.304 0 0 1-1.315 1.28H1.315c-.721 0-1.307-.57-1.315-1.28v-2.82c0-.193.16-.35.357-.35h15.286c.197 0 .357.157.357.35v2.82zM1.362 6.7A.356.356 0 0 1 1 6.35c0-.193.162-.35.362-.35h13.376c.2 0 .362.157.362.35 0 .193-.162.35-.362.35H1.362zm.622-2.925c0 .193-.16.35-.357.35a.353.353 0 0 1-.357-.35v-1.56c0-.712.59-1.29 1.316-1.29h10.828c.35 0 .684.136.93.378.247.242.386.57.386.912v1.56c0 .193-.16.35-.357.35a.353.353 0 0 1-.357-.35v-1.56a.584.584 0 0 0-.176-.417.608.608 0 0 0-.426-.173H2.586a.596.596 0 0 0-.602.59v1.56z"></path><path d="M2.19 15.06a.35.35 0 1 1 .7 0v.63a.35.35 0 1 1-.7 0v-.63zm10.62 0a.35.35 0 1 1 .7 0v.63a.35.35 0 1 1-.7 0v-.63z"></path></svg>'
        },
        // Queen-size bed

        RACLETTE: {
            text: '',
            ico: ''
        },
        // Raclette

        RADIO: {
            text: '',
            ico: ''
        },
        // Radio

        REGIONAL_FOOD: {
            text: '',
            ico: ''
        },
        // Regional food

        RENEWABLE_ENERGY: {
            text: 'Erneuerbare Energie',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="renewable_energy" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M10.22 31.219c-2.284-.089-6.27-1.462-8.016-5.201-.737-1.578-.892-3.398-.86-4.896.033-1.51.259-2.795.356-3.35l.001-.005a1 1 0 0 1 1.001-.827c.994.016 2.542.245 4.071.874 1.53.628 3.138 1.698 4.086 3.46.913 1.7 1.136 3.618 1.074 5.27-.063 1.654-.413 3.13-.734 4.016a1 1 0 0 1-.979.659Zm-.676-2.08c.184-.707.351-1.646.39-2.67.054-1.445-.15-2.971-.837-4.248-.653-1.214-1.808-2.033-3.084-2.557a10.154 10.154 0 0 0-2.486-.657 18.263 18.263 0 0 0-.183 2.158c-.03 1.37.124 2.832.672 4.007 1.157 2.476 3.694 3.66 5.528 3.968Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M24.882 7.084C19.998 2.2 12.08 2.2 7.196 7.084s-4.884 11.825 0 16.709l-1.414 1.414C.117 19.542.117 11.334 5.782 5.67c5.665-5.665 14.85-5.665 20.514 0l-1.414 1.414Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M25.672 23.854c4.375-5.345 3.588-13.225-1.757-17.599-5.346-4.374-13.225-3.587-17.6 1.758L4.769 6.746C9.842.546 18.981-.367 25.181 4.707c6.2 5.074 7.113 14.213 2.04 20.413l-1.549-1.266Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M15.006 20.392c-.716 6.618 7.54 10.193 11.877 5.144l.83-.967-1.516-1.303-.83.967c-3.058 3.559-8.877 1.039-8.373-3.626l-1.988-.215Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="m20.46 11.775-9.25-.215-.145 6.232a3.798 3.798 0 0 0 3.709 3.885l1.655.039a3.798 3.798 0 0 0 3.885-3.709l.145-6.232Zm-2.047 1.953-.098 4.233a1.798 1.798 0 0 1-1.84 1.755l-1.655-.038a1.798 1.798 0 0 1-1.755-1.84l.098-4.232 5.25.122Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="m12.779 8-.092 3.973 1.999.047.092-3.973-2-.047ZM16.965 8.097l-.093 3.974 2 .046.092-3.973-2-.047Z" clip-rule="evenodd"></path></svg>'
        },
        // Renewable energy

        RESTAURANT: {
            text: '',
            ico: ''
        },
        // Restaurant

        ROMANTIC: {
            text: '',
            ico: ''
        },
        // Romantic

        RUSTIC: {
            text: '',
            ico: ''
        },
        // Rustic

        SAFE: {
            text: '',
            ico: ''
        },
        // Safe

        SAUNA: {
            text: 'Sauna',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="sauna" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M9.51 3.214c-.097-.157-.271-.333-.645-.61l1.19-1.607c.426.315.852.675 1.155 1.164.319.515.45 1.085.45 1.74 0 .44-.127.849-.261 1.18-.136.336-.31.664-.458.94l-.022.04a9.462 9.462 0 0 0-.353.698c-.094.22-.106.32-.106.341 0 .29.06.488.16.657.109.183.297.39.635.64l-1.191 1.607c-.463-.343-.874-.737-1.165-1.23A3.217 3.217 0 0 1 8.46 7.1c0-.428.138-.826.268-1.13.124-.287.282-.58.417-.832l.033-.063a8 8 0 0 0 .367-.744c.09-.223.114-.358.114-.43 0-.374-.07-.557-.15-.687Zm5 0c-.098-.157-.271-.333-.645-.61l1.19-1.607c.426.315.852.675 1.155 1.164.319.515.45 1.085.45 1.74 0 .44-.127.849-.261 1.18-.136.336-.31.664-.458.94l-.022.04a9.462 9.462 0 0 0-.353.698c-.094.22-.106.32-.106.341 0 .29.06.488.16.657.109.183.297.39.635.64l-1.191 1.607c-.463-.343-.874-.737-1.165-1.23A3.217 3.217 0 0 1 13.46 7.1c0-.428.138-.826.268-1.13.124-.287.282-.58.417-.832l.033-.063c.153-.285.278-.523.367-.744.09-.223.114-.358.114-.43 0-.374-.07-.557-.15-.687Z"></path><path fill-rule="evenodd" d="m20.577 22.5.205-1.503A2.333 2.333 0 0 0 23 18.667v-1.334c0-.966-.587-1.795-1.425-2.15l.21-1.534A2.333 2.333 0 0 0 19.471 11H4.527a2.333 2.333 0 0 0-2.311 2.649l.209 1.535A2.334 2.334 0 0 0 1 17.334v1.333c0 1.25.983 2.27 2.218 2.33l1.088 7.985A2.333 2.333 0 0 0 6.618 31H17v-1.886a5.72 5.72 0 0 1-.047-.114h-1.866l.667-8h3.01l-.205 1.5h2.018Zm-.774-9.122L19.582 15h-3.329l.167-2h3.052c.202 0 .358.178.33.378ZM14.413 13l-.167 2H9.753l-.166-2h4.826ZM7.58 13l.167 2H4.418l-.22-1.622a.333.333 0 0 1 .33-.378H7.58Zm2.674 8h3.492l-.666 8h-2.16l-.666-8Zm-5.017 0h3.01l.666 8H6.618a.333.333 0 0 1-.33-.288L5.237 21Zm-1.904-4a.333.333 0 0 0-.333.333v1.334c0 .184.15.333.333.333h17.334c.184 0 .333-.15.333-.333v-1.334a.333.333 0 0 0-.333-.333H3.333Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M30.074 13.015a2 2 0 0 0-2.546 1.868l-.304 10.744v1.01a4.362 4.362 0 1 1-8.724 0v-1.024a1 1 0 0 1 1-1h5.752l.277-9.787c.074-2.607 2.583-4.447 5.091-3.735l.153.044-.546 1.924-.153-.044Zm-4.85 13.598H20.5v.025a2.362 2.362 0 1 0 4.724 0v-.025Z" clip-rule="evenodd"></path></svg>'
        },
        // Sauna

        SEAVIEW: {
            text: 'Meerblick',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="seaview" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M19.48 1a6.8 6.8 0 1 0 0 13.6 6.8 6.8 0 0 0 0-13.6Zm-4.8 6.8a4.8 4.8 0 1 1 9.6 0 4.8 4.8 0 0 1-9.6 0Z" clip-rule="evenodd"></path><path d="M12.52 23.04a2.48 2.48 0 0 1-2.48-2.48h-2a2.48 2.48 0 1 1-4.96 0h-2a4.48 4.48 0 0 0 7.96 2.822 4.471 4.471 0 0 0 3.48 1.658A4.471 4.471 0 0 0 16 23.382a4.471 4.471 0 0 0 3.48 1.658 4.471 4.471 0 0 0 3.48-1.658 4.48 4.48 0 0 0 7.96-2.822h-2a2.48 2.48 0 1 1-4.96 0h-2a2.48 2.48 0 1 1-4.96 0h-2a2.48 2.48 0 0 1-2.48 2.48Zm-4.48 4.48a2.48 2.48 0 1 1-4.96 0h-2a4.48 4.48 0 0 0 7.96 2.822A4.471 4.471 0 0 0 12.52 32 4.471 4.471 0 0 0 16 30.342 4.471 4.471 0 0 0 19.48 32a4.471 4.471 0 0 0 3.48-1.658 4.48 4.48 0 0 0 7.96-2.822h-2a2.48 2.48 0 0 1-4.96.003v-.003h-2v.003A2.48 2.48 0 0 1 17 27.52h-2a2.48 2.48 0 1 1-4.96 0h-2Z"></path></svg>'
        },
        // Ocean view

        SMOKE_ALARM: {
            text: 'Rauchmelder',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="smoke_alarm" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M16.587 11.055c1.12.77 1.458 1.414 1.528 1.97.079.624-.14 1.404-.702 2.528-.609 1.218-1.006 2.456-.815 3.7.198 1.283.983 2.356 2.264 3.316l1.2-1.6c-1.028-.77-1.396-1.428-1.487-2.02-.097-.63.082-1.412.627-2.502.592-1.183 1.055-2.423.897-3.673-.166-1.32-.983-2.407-2.378-3.367l-1.134 1.648ZM13.5 13.024c-.07-.555-.408-1.199-1.528-1.97l1.133-1.647c1.396.96 2.212 2.047 2.379 3.367.157 1.25-.306 2.49-.897 3.673-.545 1.09-.724 1.871-.627 2.502.09.592.458 1.25 1.486 2.02l-1.2 1.6c-1.28-.96-2.066-2.033-2.263-3.316-.192-1.244.206-2.482.815-3.7.562-1.124.78-1.904.701-2.529Zm11.17 6.567a9.352 9.352 0 0 1-5.079 5.08l.766 1.847a11.352 11.352 0 0 0 6.161-6.161l-1.848-.766ZM19.591 7.33a9.352 9.352 0 0 1 5.08 5.079l1.847-.766a11.352 11.352 0 0 0-6.161-6.161L19.59 7.33Zm-7.182 17.34a9.352 9.352 0 0 1-5.08-5.079l-1.847.766a11.352 11.352 0 0 0 6.161 6.16l.766-1.847ZM7.33 12.409a9.352 9.352 0 0 1 5.079-5.08l-.766-1.847a11.352 11.352 0 0 0-6.161 6.161l1.848.766Z"></path><path fill-rule="evenodd" d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0ZM2 16C2 8.268 8.268 2 16 2s14 6.268 14 14-6.268 14-14 14S2 23.732 2 16Z" clip-rule="evenodd"></path></svg>'
        },
        // Smoke alarm

        SMOKING_ALLOWED: {
            text: 'Rauchen erlaubt',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="smoking_allowed" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M22.385 6.154c0-1.265-.788-1.949-2.057-2.889l1.19-1.607.014.01c1.19.882 2.853 2.113 2.853 4.486 0 .688-.234 1.283-.516 1.777-.27.472-.618.91-.91 1.28l-.022.026c-.318.4-.575.727-.76 1.053-.18.315-.254.564-.254.787 0 1.265.787 1.948 2.057 2.889l-1.19 1.607-.014-.01c-1.191-.883-2.853-2.113-2.853-4.486 0-.689.234-1.283.516-1.778.27-.471.617-.91.91-1.279l.022-.026c.318-.4.574-.728.76-1.054.18-.314.254-.563.254-.786Zm6.153 2.461c0-1.265-.787-1.948-2.056-2.888l1.19-1.607.014.01c1.19.882 2.852 2.113 2.852 4.485 0 .689-.234 1.284-.515 1.778-.27.471-.618.91-.911 1.279l-.021.027c-.318.4-.575.727-.76 1.053-.18.315-.254.563-.254.786 0 1.265.787 1.949 2.057 2.89l-1.19 1.606-.014-.01c-1.191-.882-2.853-2.113-2.853-4.486 0-.688.234-1.283.516-1.777.269-.472.617-.91.91-1.28l.022-.026c.318-.4.574-.727.76-1.053.18-.315.253-.564.253-.787Z"></path><path fill-rule="evenodd" d="M1.23 19.923a1 1 0 0 0-1 1v6.154a1 1 0 0 0 1 1h29.54a1 1 0 0 0 1-1v-6.154a1 1 0 0 0-1-1H1.231Zm18.693 6.154v-4.154H2.231v4.154h17.692Zm4.923 0h-2.923v-4.154h2.923v4.154Zm2 0v-4.154h2.923v4.154h-2.923Z" clip-rule="evenodd"></path></svg>'
        },
        // Smoking allowed

        SMOKING_NOT_ALLOWED: {
            text: 'Nichtraucher',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="smoking_not_allowed" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M20.328 3.265c1.27.94 2.057 1.624 2.057 2.889 0 .223-.074.472-.254.786-.186.326-.442.653-.76 1.054l-.021.026c-.294.37-.642.808-.91 1.28-.283.494-.517 1.088-.517 1.777 0 2.373 1.662 3.604 2.853 4.486l.013.01 1.19-1.607c-1.269-.94-2.056-1.624-2.056-2.89 0-.222.074-.47.253-.786.186-.326.443-.652.761-1.053l.021-.026c.293-.37.642-.808.91-1.28.283-.494.517-1.089.517-1.777 0-2.373-1.662-3.604-2.853-4.486l-.014-.01-1.19 1.607Zm6.154 2.462c1.27.94 2.056 1.623 2.056 2.888 0 .223-.073.472-.253.787-.186.326-.442.653-.76 1.053l-.022.027c-.293.369-.641.807-.91 1.279-.282.494-.516 1.089-.516 1.777 0 2.373 1.662 3.604 2.853 4.486l.013.01 1.19-1.607c-1.269-.94-2.056-1.623-2.056-2.889 0-.223.074-.471.253-.786.186-.326.443-.653.76-1.053l.022-.027c.293-.369.641-.808.91-1.28.282-.493.517-1.088.517-1.777 0-2.372-1.662-3.603-2.854-4.486l-.013-.01-1.19 1.608Zm-2.648 22.35-3.91-3.911v1.91H2.23v-4.153h15.45l-2-2H1.23a1 1 0 0 0-1 1v6.154a1 1 0 0 0 1 1h22.603Zm.332-8.154h6.603a1 1 0 0 1 1 1v6.154a1 1 0 0 1-.073.376l-1.927-1.926v-3.604h-2.923v.68l-2.68-2.68ZM3.293 4.707l27 27 1.414-1.414-27-27-1.414 1.414Z"></path></svg>'
        },
        // No smoking

        SOLARIUM: {
            text: 'Solarium',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="solarium" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M3.9 9.345C6.01 5.01 10.546 2 15.817 2 23.12 2 29 7.771 29 14.838c0 2.233-.584 4.332-1.614 6.162h-.41a2.29 2.29 0 0 0 .024-.333v-.46a2.333 2.333 0 0 0-2.101-2.322l-4.496-.45a4.5 4.5 0 1 0-8.805 0l-4.497.45A2.333 2.333 0 0 0 5 20.207v.46c0 .113.008.224.024.333h-1.01a2.333 2.333 0 0 0-2.148 3.245l2.687 6.333A2.333 2.333 0 0 0 6.7 32h18.598c.937 0 1.782-.56 2.148-1.422l2.687-6.333a2.332 2.332 0 0 0-.734-2.77 14.507 14.507 0 0 0 1.6-6.637C31 6.62 24.18 0 15.818 0 9.773 0 4.543 3.455 2.1 8.47l1.798.875ZM16 21a4.49 4.49 0 0 0 3.48-1.647l5.22.522c.17.017.3.16.3.332v.46a.333.333 0 0 1-.33.333H16Zm0 0H7.33a.333.333 0 0 1-.33-.333v-.46c0-.172.13-.315.3-.332l5.22-.522A4.491 4.491 0 0 0 16 21Zm-8.677 2H4.014c-.239 0-.4.244-.307.463l2.687 6.334a.333.333 0 0 0 .307.203h18.598c.134 0 .255-.08.307-.203l2.687-6.334a.333.333 0 0 0-.307-.463H7.323ZM16 19a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" clip-rule="evenodd"></path><path d="M23 12h2V8h-2v4Zm-4-2V5h2v5h-2Zm-4-7v6h2V3h-2Zm-4 7V5h2v5h-2ZM7 8v4h2V8H7Z"></path></svg>'
        },
        // Solarium

        SOLAR_POWER: {
            text: '',
            ico: ''
        },
        // Solar power

        SPA: {
            text: '',
            ico: ''
        },
        // Spa

        STEREO: {
            text: '',
            ico: ''
        },
        // Sound system/stereo

        SUITABLE_FOR_EVENTS: {
            text: '',
            ico: ''
        },
        // Suitable for events

        SUPPORT_LOCAL_COMMUNITY: {
            text: '',
            ico: ''
        },
        // Supports local community

        SURF: {
            text: 'Surfen',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="surf" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M4.769 1.68C5.559.717 6.579 0 7.8 0c1.222 0 2.24.716 3.031 1.68.796.973 1.47 2.318 2.015 3.893C13.941 8.73 14.6 13.03 14.6 17.73c0 5.488-.899 10.448-2.355 13.68a1 1 0 0 1-.912.589H4.267a1 1 0 0 1-.912-.59C1.9 28.18 1 23.22 1 17.73c0-4.7.659-8.999 1.754-12.157.546-1.575 1.218-2.92 2.015-3.892Zm-.125 4.548C3.639 9.125 3 13.192 3 17.73c0 4.972.767 9.36 1.929 12.269H6.8V17.154h2V30h1.871c1.162-2.908 1.929-7.297 1.929-12.27 0-4.538-.64-8.605-1.644-11.502-.503-1.453-1.08-2.558-1.672-3.28C8.687 2.22 8.18 2 7.8 2c-.38 0-.887.22-1.484.949-.592.721-1.169 1.826-1.672 3.279Z" clip-rule="evenodd"></path><path d="M20.56 23.077a2.47 2.47 0 0 0 2.48-2.462h2a2.47 2.47 0 0 0 2.48 2.462A2.47 2.47 0 0 0 30 20.615h2c0 2.47-2.01 4.462-4.48 4.462a4.48 4.48 0 0 1-3.48-1.652 4.48 4.48 0 0 1-3.48 1.652c-2.47 0-4.48-1.993-4.48-4.462h2a2.47 2.47 0 0 0 2.48 2.462Zm0 6.923a2.47 2.47 0 0 1-2.48-2.462h-2c0 2.47 2.01 4.462 4.48 4.462a4.48 4.48 0 0 0 3.48-1.651A4.48 4.48 0 0 0 27.52 32c2.47 0 4.48-1.992 4.48-4.462h-2A2.47 2.47 0 0 1 27.52 30a2.47 2.47 0 0 1-2.48-2.457v-.005h-2v.005A2.47 2.47 0 0 1 20.56 30Z"></path></svg>'
        },
        // Surfing

        TABLE_TENNIS: {
            text: '',
            ico: ''
        },
        // Table tennis

        TELEPHONE: {
            text: '',
            ico: ''
        },
        // Telephone

        TENNIS: {
            text: '',
            ico: ''
        },
        // Tennis courts

        TERRACE: {
            text: 'Terrasse/Balkon',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="terrace" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M8.462 1a1 1 0 0 0-1 1v14.077H2v2h2.23V30a1 1 0 0 0 1 1h21.54a1 1 0 0 0 1-1V18.077H30v-2h-5.462V2a1 1 0 0 0-1-1H8.462ZM6.23 29V18.077h3.384V29H6.231Zm5.384 0H15V18.077h-3.385V29ZM17 29h3.385V18.077H17V29Zm8.77 0h-3.385V18.077h3.384V29ZM9.461 16.077v-5.539H15v5.539H9.462Zm7.538 0h5.538v-5.539H17v5.539Zm0-7.539h5.538V3H17v5.538Zm-2 0H9.462V3H15v5.538Z" clip-rule="evenodd"></path></svg>'
        },
        // Balcony/patio

        TOASTER: {
            text: 'Toaster',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="toaster" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M23.273 21.727h-2.546v-2h2.546v-7.273h2v11.819h-2v-2.546Z"></path><path fill-rule="evenodd" d="M6.727 5.364A3.364 3.364 0 0 1 10.091 2h3.545c.922 0 1.757.37 2.364.97.608-.6 1.442-.97 2.364-.97h3.545a3.364 3.364 0 0 1 3.364 3.364v2.545h1.41c1.104 0 2.106.709 2.438 1.789 1.918 6.225 2.05 11.112 2.06 17.724A2.575 2.575 0 0 1 28.606 30H3.394a2.575 2.575 0 0 1-2.575-2.578c.01-6.588.143-11.498 2.06-17.724a2.546 2.546 0 0 1 2.439-1.789h1.41V5.364ZM10.091 4c-.753 0-1.364.61-1.364 1.364v2.545H15V5.364C15 4.61 14.39 4 13.636 4h-3.545Zm18.515 24a.575.575 0 0 0 .575-.575c-.01-6.584-.147-11.216-1.971-17.139a.546.546 0 0 0-.528-.377H5.318a.547.547 0 0 0-.528.377c-1.823 5.924-1.96 10.58-1.97 17.139 0 .317.257.575.575.575h25.212ZM17 5.364C17 4.61 17.61 4 18.364 4h3.545c.753 0 1.364.61 1.364 1.364v2.545H17V5.364Z" clip-rule="evenodd"></path></svg>'
        },
        // Toaster

        TOWELS: {
            text: 'Handtücher',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="towels" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M4.673 16A7.5 7.5 0 0 1 8.417 2H25.75a3.167 3.167 0 0 1 2.583 5 5.332 5.332 0 0 1 2.75 4.667 5.332 5.332 0 0 1-2.75 4.667c.368.517.584 1.15.584 1.833s-.216 1.315-.584 1.832a5.333 5.333 0 0 1-2.583 10H8.417A7.5 7.5 0 0 1 4.673 16ZM25.75 6.333a1.167 1.167 0 0 0 0-2.333h-8.667a5.5 5.5 0 0 0 0 11h8.667a3.333 3.333 0 1 0 0-6.667h-8.667a1.167 1.167 0 0 0 0 2.334h8.667a1 1 0 1 1 0 2h-8.677a3.167 3.167 0 0 1 .01-6.334h8.667ZM9.583 9.5A7.48 7.48 0 0 1 11.984 4H8.417a5.5 5.5 0 0 0 0 11h3.567a7.48 7.48 0 0 1-2.4-5.5Zm-6.666 13a5.5 5.5 0 0 1 5.5-5.5h3.567a7.48 7.48 0 0 0-2.4 5.5 7.48 7.48 0 0 0 2.4 5.5H8.417a5.5 5.5 0 0 1-5.5-5.5Zm8.666 0a5.5 5.5 0 0 1 5.5-5.5h8.667a1.167 1.167 0 0 1 .004 2.333h-8.671a3.167 3.167 0 0 0 0 6.334h8.667a1 1 0 0 0 0-2h-8.672a1.167 1.167 0 0 1 .005-2.334h8.667a3.333 3.333 0 1 1 0 6.667h-8.667a5.5 5.5 0 0 1-5.5-5.5Z" clip-rule="evenodd"></path></svg>'
        },
        // Towels

        TOWELS_FREE: {
            text: '',
            ico: ''
        },
        // Towels (free)

        TOWELS_SURCHARGE: {
            text: '',
            ico: ''
        },
        // Towels (surcharge)

        TRAMPOLINE: {
            text: '',
            ico: ''
        },
        // Trampoline

        TV: {
            text: 'TV',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="tv" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M32 7a3 3 0 0 0-3-3H3a3 3 0 0 0-3 3v15a3 3 0 0 0 3 3h3.393l-2.13 2.324A1 1 0 0 0 5 29h22a1 1 0 0 0 .737-1.676L25.607 25H29a3 3 0 0 0 3-3V7Zm-9.107 18H9.107l-1.834 2h17.454l-1.834-2ZM29 6a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h26Z" clip-rule="evenodd"></path></svg>'
        },
        // TV

        VALENTINES_DAY: {
            text: '',
            ico: ''
        },
        // Special gift for Valentine's Day

        VIDEO_GAMES: {
            text: '',
            ico: ''
        },
        // Video games

        VIEW_BEACH: {
            text: 'Blick auf den Strand',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="view_beach" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M26.035 0A6.966 6.966 0 0 0 33 6.966v2A8.966 8.966 0 0 1 24.035 0h2Zm-.293 8.673-4.552 4.551-1.414-1.414 4.551-4.552 1.415 1.415Zm3.074 6.471 1.707-4.552-1.873-.702-1.707 4.552 1.873.702ZM23.11 4.35l-4.552 1.707-.702-1.873 4.551-1.707.703 1.873Z"></path><path fill-rule="evenodd" d="m7.19 4.64.448 1.595 7.056 1.552a1 1 0 0 1 .75.718l.235.873a1 1 0 0 1-.406 1.087l-.56-.828.56.828-.002.002-.003.002-.008.005-.027.018a11.645 11.645 0 0 1-.442.27c-.302.175-.742.416-1.317.692a27.994 27.994 0 0 1-3.943 1.523l2.906 10.35H10.36L7.6 13.502c-1.785.425-3.28.617-4.377.7-.635.048-1.138.06-1.487.059a11.503 11.503 0 0 1-.517-.013l-.032-.002h-.015s-.001-.001.07-.998l-.071.997a1 1 0 0 1-.895-.739l-.234-.873a1 1 0 0 1 .291-.997L5.7 6.733 5.264 5.18l1.926-.54Zm-4.117 7.567c-.153.011-.296.02-.43.027l4.456-4.07 5.895 1.297c-.12.06-.249.125-.387.19-1.05.505-2.616 1.152-4.655 1.698-2.038.546-3.718.77-4.88.858Z" clip-rule="evenodd"></path><path d="M0 20.62h1.732c1.01 0 1.965.467 2.586 1.265l2.999 3.856h17.315c1.34 0 2.544.816 3.041 2.06l1.625 4.061h-2.154l-1.328-3.319a1.276 1.276 0 0 0-1.184-.802H7.505l-1.649 4.121H3.702l1.984-4.96-2.947-3.789a1.276 1.276 0 0 0-1.007-.492H0v-2Z"></path></svg>'
        },
        // Beach view

        VIEW_CITY: {
            text: 'Stadtblick',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="view_city" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M5.25 6.333h3.083v2H5.25v-2Zm3.083 4.334H5.25v2h3.083v-2Zm0 4.333H5.25v2h3.083v-2ZM5.25 19.333h3.083v2H5.25v-2Z"></path><path fill-rule="evenodd" d="M3 2C1.85 2 .917 2.933.917 4.083V29a1 1 0 0 0 1 1h28.166a1 1 0 0 0 1-1V8.417c0-1.15-.932-2.084-2.083-2.084h-8.667c-1.15 0-2.083.933-2.083 2.084V15h-2.333V4.083c0-1.15-.933-2.083-2.084-2.083H3Zm10.917 13v-2.333h-3.25v-2h3.25V8.333h-3.25v-2h3.25v-2.25A.083.083 0 0 0 13.833 4H3a.083.083 0 0 0-.083.083V28h7.75V17.083c0-1.15.932-2.083 2.083-2.083h1.167Zm6.333 0h.083c1.15 0 2.084.933 2.084 2.083V28h6.666v-6.667H24.75v-2h4.333V17H24.75v-2h4.333v-2.333H24.75v-2h4.333v-2.25A.083.083 0 0 0 29 8.333h-8.667a.083.083 0 0 0-.083.084V15Zm-7.583 2.083c0-.046.037-.083.083-.083h7.583c.046 0 .084.037.084.083v2.25h-4.334v2h4.334v2.334h-4.334v2h4.334V28h-7.75V17.083Z" clip-rule="evenodd"></path></svg>'
        },
        // City view

        VIEW_GARDEN: {
            text: 'Gartenblick',
            ico: '<svg width="16" height="16" viewBox="0 0 16 16" icon="view_garden" class="fxsh0 w16 h16 f-gray-extra-dark"><path d="M9.892 11.008c.201.005.396.068.56.183l1.121.754-1.13.754a.876.876 0 0 1-.55.184.937.937 0 1 1 0-1.875zm0-.703a1.64 1.64 0 1 0 0 3.28 1.61 1.61 0 0 0 1.02-.325l1.049-.724a.723.723 0 0 0 .315-.59.693.693 0 0 0-.315-.581l-1.121-.754a1.569 1.569 0 0 0-.948-.306zm.368-6.317v2.619a2.262 2.262 0 0 1-4.504 0V3.988a1.11 1.11 0 0 1 1.12.938h.194a.937.937 0 0 1 1.875 0h.193a1.11 1.11 0 0 1 1.121-.938zm0-.703a1.977 1.977 0 0 0-1.173.398 1.67 1.67 0 0 0-2.16 0 1.977 1.977 0 0 0-1.171-.398.703.703 0 0 0-.703.703v2.619a2.965 2.965 0 0 0 5.91 0V3.988a.703.703 0 0 0-.704-.703z"></path><path d="M7.656 8.97h.703v5.094h-.703z"></path><path d="M6.123 10.254a.948.948 0 1 1 0 1.885.886.886 0 0 1-.55-.194l-1.131-.754 1.12-.692c.108-.083.294-.164.56-.245zm0-.652a1.559 1.559 0 0 0-1.02.336l-1.09.724a.703.703 0 0 0-.315.58c.002.237.12.458.316.591l1.12.754a1.64 1.64 0 1 0 .948-2.985h.04z"></path><path d="M.718 2.19v11.523h14.58V2.19H.717zm-.352-.703H15.65c.194 0 .351.157.351.352v12.225a.351.351 0 0 1-.351.352H.366a.351.351 0 0 1-.351-.352V1.84c0-.195.157-.352.351-.352z"></path></svg>'
        },
        // Garden view

        VIEW_GOLF: {
            text: '',
            ico: ''
        },
        // Golf course view

        VIEW_LAKE: {
            text: '',
            ico: ''
        },
        // Lake view

        VIEW_MOUNTAIN: {
            text: 'Bergblick',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="view_mountain" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M18.002 1.612a1 1 0 0 0-1.838-.013L8.97 18.043l-2.374-.792a1 1 0 0 0-1.202.486l-5.08 9.72a1 1 0 0 0-.114.463V29a1 1 0 0 0 1 1h29.6a1 1 0 0 0 1-1v-1.08a1 1 0 0 0-.038-.273l-1.84-6.48a1 1 0 0 0-1.278-.676l-2.362.788-8.28-19.667Zm-4.327 10.666 3.387-7.743 3.305 7.85L19 13.614l-1.731-1.558a1 1 0 0 0-1.338 0l-1.251 1.126-1.005-.905Zm-.845 1.93-2.394 5.473a1 1 0 0 1-1.232.548l-2.432-.811L2.287 28h27.496l-1.5-5.28-2.247.749a1 1 0 0 1-1.238-.56l-3.61-8.574-1.519 1.368a1 1 0 0 1-1.338 0L16.6 14.145l-1.251 1.126a1 1 0 0 1-1.338 0l-1.18-1.062Z" clip-rule="evenodd"></path></svg>'
        },
        // Mountain view

        VIEW_NATURE: {
            text: '',
            ico: ''
        },
        // Nature view

        VIEW_PANORAMIC: {
            text: 'Panoramablick',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="view_panoramic" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M.41 5.376a1 1 0 0 1 .895-.145C5.138 6.46 10.298 7.223 16 7.223s10.862-.763 14.695-1.992A1 1 0 0 1 32 6.184v19.632a1 1 0 0 1-1.305.953C26.862 25.54 21.702 24.777 16 24.777s-10.862.763-14.695 1.992A1 1 0 0 1 0 25.816V6.184a1 1 0 0 1 .41-.808ZM2 7.524v16.952c3.884-1.07 8.746-1.699 14-1.699s10.116.629 14 1.7V7.523c-3.884 1.07-8.746 1.7-14 1.7s-10.116-.63-14-1.7Z" clip-rule="evenodd"></path></svg>'
        },
        // Panoramic view

        VIEW_POOL: {
            text: '',
            ico: ''
        },
        // Pool view

        VIEW_RIVER: {
            text: '',
            ico: ''
        },
        // River view

        VIEW_WATER: {
            text: '',
            ico: ''
        },
        // Water view

        WASHING_MACHINE_AVAILABLE: {
            text: 'Waschmaschine',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="washing_machine_available" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M16 8a9.167 9.167 0 1 0 0 18.333A9.167 9.167 0 0 0 16 8Zm-7.167 9.167a7.167 7.167 0 1 1 14.308.614c-1.068 1.085-1.953 1.886-3.712 1.886-.856 0-1.772-.444-2.974-1.058l-.1-.05c-1.067-.546-2.397-1.226-3.784-1.226-1.584 0-2.72.49-3.62 1.131a7.208 7.208 0 0 1-.118-1.297Zm.805 3.303A7.166 7.166 0 0 0 16 24.333a7.16 7.16 0 0 0 5.948-3.167c-.697.305-1.519.5-2.52.5-1.386 0-2.716-.679-3.782-1.224l-.1-.051c-1.203-.614-2.12-1.058-2.975-1.058-1.321 0-2.15.452-2.933 1.137Z" clip-rule="evenodd"></path><path d="M20.833 5.667h4.334v2h-4.334v-2Z"></path><path fill-rule="evenodd" d="M4.556 1A2.556 2.556 0 0 0 2 3.556v24.888A2.556 2.556 0 0 0 4.556 31h22.888A2.556 2.556 0 0 0 30 28.444V3.556A2.556 2.556 0 0 0 27.444 1H4.556ZM4 3.556C4 3.249 4.249 3 4.556 3h22.888c.307 0 .556.249.556.556v24.888a.556.556 0 0 1-.556.556H4.556A.556.556 0 0 1 4 28.444V3.556Z" clip-rule="evenodd"></path></svg>'
        },
        // Washing machine

        WASTE_RECYCLING: {
            text: 'Recycling vorhanden',
            ico: '<svg width="16" height="16" viewBox="0 0 28 30" icon="waste_recycling" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M1 0h25.714a1 1 0 0 1 .995 1.107l-2.877 26.85A2.286 2.286 0 0 1 22.559 30H5.155a2.286 2.286 0 0 1-2.272-2.042L.006 1.107A1 1 0 0 1 1 0Zm1.113 2L4.87 27.745a.286.286 0 0 0 .284.255H22.56c.146 0 .268-.11.284-.255L25.601 2H2.113Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M22.341 17.919c.837 1.332-.12 3.064-1.693 3.064H14.55v-2h6.098l-3.023-4.81 1.693-1.065 3.023 4.81ZM7.054 20.738c-1.567 0-2.525-1.72-1.7-3.052l2.822-4.563 1.7 1.053-2.822 4.562h5.621v2h-5.62ZM11.797 6.436c.771-1.336 2.7-1.333 3.467.006l3.065 5.353-1.735.994-3.065-5.353-2.838 4.916-1.733-1 2.839-4.916Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="m13.834 10.1 2.88.771.771-2.88 1.932.518-1.29 4.812-4.811-1.29.518-1.931ZM9.052 17.537l-.771-2.88-2.88.772-.518-1.932 4.812-1.29 1.29 4.813-1.933.517ZM18.063 17.649l-2.108 2.108 2.108 2.108-1.414 1.415-3.523-3.523 3.523-3.522 1.414 1.414Z" clip-rule="evenodd"></path></svg>'
        },
        // Recycling available

        WATERFRONT: {
            text: '',
            ico: ''
        },
        // Waterfront

        WATER_EFFICIENT_BATHROOM: {
            text: '',
            ico: ''
        },
        // Water efficient bathroom

        WATER_SAVING_APPLIANCES: {
            text: '',
            ico: ''
        },
        // Water efficient appliances

        WEDDING: {
            text: '',
            ico: ''
        },
        // Available for weddings

        WELLNESS: {
            text: 'Wellness',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="wellness" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M16.67 3.758a1 1 0 0 0-1.34 0c-2.221 2.004-3.616 4.465-4.419 6.87a15.524 15.524 0 0 0-4.544-1.124 1 1 0 0 0-.949.479c-.95 1.572-1.362 3.14-1.414 4.641a9.142 9.142 0 0 0-3.539 1.297 1 1 0 0 0-.465.845c0 3.31 1.623 6.847 4.448 9.161 2.832 2.321 6.844 3.39 11.552 1.855 4.708 1.535 8.72.466 11.552-1.855C30.377 23.613 32 20.076 32 16.766a1 1 0 0 0-.465-.845 9.147 9.147 0 0 0-3.54-1.297c-.05-1.501-.463-3.069-1.413-4.641a1 1 0 0 0-.95-.479 15.525 15.525 0 0 0-4.543 1.124c-.803-2.405-2.198-4.866-4.419-6.87Zm4.95 8.82c.265 1.248.38 2.441.38 3.5 0 1.798-.329 4.108-1.164 6.215a11.973 11.973 0 0 1-1.207 2.313c1.99-.97 3.664-2.602 4.672-4.27 1.53-2.532 2.457-5.646.876-8.765a13.455 13.455 0 0 0-3.558 1.006Zm-9.25 12.027a11.973 11.973 0 0 1-1.207-2.313C10.33 20.186 10 17.876 10 16.079c0-1.06.115-2.253.38-3.502a13.455 13.455 0 0 0-3.557-1.007c-1.581 3.12-.654 6.234.876 8.766 1.008 1.668 2.682 3.3 4.672 4.27ZM12 16.079c0-2.972 1.031-7.093 4-10.187 2.968 3.094 4 7.215 4 10.187 0 1.588-.296 3.642-1.023 5.476-.665 1.678-1.644 3.062-2.977 3.824-1.333-.762-2.312-2.146-2.977-3.824C12.296 19.721 12 17.667 12 16.08ZM5.987 21.37c-.822-1.36-1.547-2.98-1.845-4.742a7.254 7.254 0 0 0-2.123.704c.173 2.542 1.491 5.241 3.696 7.048 1.56 1.278 3.577 2.12 5.979 2.12-2.494-1.16-4.519-3.163-5.707-5.13Zm20.298 3.01c-1.56 1.278-3.577 2.12-5.979 2.12 2.494-1.16 4.519-3.163 5.707-5.13.822-1.36 1.547-2.98 1.845-4.742.735.127 1.45.354 2.123.704-.173 2.542-1.491 5.241-3.696 7.048Z" clip-rule="evenodd"></path></svg>'
        },
        // Wellness

        WHIRLPOOL: {
            text: 'Whirlpool',
            ico: '<svg width="16" height="16" viewBox="0 0 32 32" icon="whirlpool" class="fxsh0 w16 h16 f-gray-extra-dark"><path fill-rule="evenodd" d="M10 0a7 7 0 1 1 0 14 7 7 0 0 1 0-14Zm5 7A5 5 0 1 0 5 7a5 5 0 0 0 10 0ZM7.5 26A2.75 2.75 0 1 1 2 26H0a4.75 4.75 0 0 0 8.5 2.916 4.742 4.742 0 0 0 3.75 1.834c1.524 0 2.88-.718 3.75-1.834a4.742 4.742 0 0 0 3.75 1.834c1.524 0 2.88-.718 3.75-1.834A4.75 4.75 0 0 0 32 26h-2a2.75 2.75 0 1 1-5.5 0h-2a2.75 2.75 0 1 1-5.5 0h-.006a4.75 4.75 0 1 0-9.487 0H7.5Zm7.5-.25a2.75 2.75 0 1 0-5.5 0 2.75 2.75 0 0 0 5.5 0Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M29.5 17a6 6 0 1 0-12 0 6 6 0 0 0 12 0Zm-6-4a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z" clip-rule="evenodd"></path></svg>'
        },
        // Hot tub

        WINE_CELLAR: {
            text: '',
            ico: ''
        },
        // Wine cellar
    }
}

